import { forwardRef, useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import {
  Flex,
  ImagePlusIcon,
  Text,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import CircularUploadIndicator from '~publish/legacy/composer/composer/components/progress-indicators/CircularUploadIndicator'
import { useAnimatedVisibility } from '~publish/legacy/hooks/useAnimatedVisibility'

import { FileInput } from '../FileInput'
import { MediaThumbnail } from '../MediaThumbnail'

import styles from './UploadButton.module.css'

export function isChangeEventFileTransfer(
  event: React.ChangeEvent<HTMLInputElement>,
): boolean {
  return !!event.target.files
}

export type UploadButtonProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    accept?: string
    /**
     * Whether the button is in a busy state, e.g. uploading
     */
    busy?: boolean
    /**
     * The progress of all pending uploads
     */
    progress?: number
    /**
     * The number of pending uploads
     */
    pendingCount?: number
    /**
     * Whether the button allows multiple files to be uploaded
     */
    multiple?: boolean
    /**
     * The URL of the preview image
     */
    previewSrc?: string
    /**
     * Callback function to handle file change events
     */
    onFileChange?: (files: File[]) => void
  }

/**
 * A styled upload button that allows users to upload files
 * It displays either the button copy, or an upload progress indicator
 * Can display a preview image if a previewSrc is provided
 */
export const UploadButton = forwardRef<HTMLButtonElement, UploadButtonProps>(
  (
    {
      busy = false,
      progress = 0,
      pendingCount = 0,
      multiple,
      accept,
      previewSrc,
      onFileChange,
      ...props
    },
    ref,
  ): JSX.Element => {
    // Store the preview src to allow fade out transition to complete before
    // img src becomes undefined
    const [previewSrcCache, setPreviewSrcCache] = useState<string | undefined>()

    useEffect(() => {
      if (previewSrc) setPreviewSrcCache(previewSrc)
    }, [previewSrc])

    // Using animated visibility helper adds a second layer of visibility control.
    // With only the state cache, rapidly changing the previewSrc doesn't allow the
    // previous src to fade out and the img element does a hard swap on the src value.
    const {
      isVisibleInDOM: shouldRenderPreview,
      shouldBeVisible: canAnimatePreview,
      onAnimationEnd: onAnimationEndPreview,
    } = useAnimatedVisibility({
      visibilityCondition: !!previewSrc,
    })

    const handlePreviewAnimationEnd = useCallback(() => {
      // Now that the fade out transition has completed, clear the preview src cache
      if (!previewSrc) {
        setPreviewSrcCache(undefined)
      }
      onAnimationEndPreview()
    }, [previewSrc, onAnimationEndPreview])

    const handleFileChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault()
        event.stopPropagation()

        if (props.disabled || !isChangeEventFileTransfer(event)) return

        // @TODO: consider using getDroppedFiles approach
        // @see https://github.com/transloadit/uppy/tree/main/packages/%40uppy/utils/src/getDroppedFiles
        const files: File[] = Array.from(event.target.files ?? [])
        onFileChange?.(files)
      },
      [props.disabled, onFileChange],
    )

    return (
      <FileInput
        onChange={handleFileChange}
        accept={accept}
        disabled={props.disabled}
        multiple={multiple}
      >
        <UnstyledButton
          ref={ref}
          type="button"
          className={styles.uploadButton}
          {...props}
          aria-busy={busy}
        >
          <VisuallyHidden>
            {busy ? 'Uploading' : 'Select to upload files'}
          </VisuallyHidden>
          {/* If not busy, show upload button */}
          {!busy ? (
            <>
              <Flex
                direction="column"
                gap="xs"
                align="center"
                className={styles.content}
                data-has-preview={previewSrc ? 'true' : 'false'}
              >
                <ImagePlusIcon size="medium" color="subtle" />
                <Text color="subtle">
                  Drag & drop or{' '}
                  <Text className={styles.linkText}>select a file</Text>
                </Text>
              </Flex>
              {shouldRenderPreview && previewSrcCache && (
                <div className={styles.previewWrapper}>
                  <MediaThumbnail
                    src={previewSrcCache}
                    alt="Temp image"
                    className={styles.previewImage}
                    data-state={canAnimatePreview ? 'visible' : 'hidden'}
                    onAnimationEnd={handlePreviewAnimationEnd}
                  />
                </div>
              )}
            </>
          ) : (
            // If busy, show upload progress
            <>
              <CircularUploadIndicator
                data-testid="uploads-dropzone-progress"
                size={54}
                progress={progress}
              />
              {pendingCount > 1 && (
                <Text className={styles.pendingCount} size="sm">
                  {pendingCount}
                </Text>
              )}
            </>
          )}
        </UnstyledButton>
      </FileInput>
    )
  },
)
UploadButton.displayName = 'UploadButton'
